import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import '@/App.css';
import Login from './pages/Login';
import Layout from './components/Layout';
import { authApi } from './api/auth';
import { EntityProvider } from './contexts/EntityContext';

// Lazy-load all authenticated pages -> bundle awal ringan, navigasi pertama ke setiap halaman
// memuat chunk-nya secara on-demand. HR.js (3000+ lines) kini tidak ikut di-parse saat startup.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Cashflow = lazy(() => import('./pages/Cashflow'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Debts = lazy(() => import('./pages/Debts'));
const Receivables = lazy(() => import('./pages/Receivables'));
const Investors = lazy(() => import('./pages/Investors'));
const Taxes = lazy(() => import('./pages/Taxes'));
const HR = lazy(() => import('./pages/HR'));
const Operational = lazy(() => import('./pages/Operational'));
const SPSIPage = lazy(() => import('./pages/SPSIPage'));
const OtherIncome = lazy(() => import('./pages/OtherIncome'));
const Reports = lazy(() => import('./pages/Reports'));
const ProfitLoss = lazy(() => import('./pages/ProfitLoss'));
const StockAdjustment = lazy(() => import('./pages/StockAdjustment'));
const BulkImport = lazy(() => import('./pages/BulkImport'));
const MenuSettings = lazy(() => import('./pages/MenuSettings'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const FieldTransactions = lazy(() => import('./pages/FieldTransactions'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Settings = lazy(() => import('./pages/Settings'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'));
const OwnerExpenses = lazy(() => import('./pages/OwnerExpenses'));
const DataMigration = lazy(() => import('./pages/DataMigration'));
const IntelligenceLab = lazy(() => import('./pages/IntelligenceLab'));
const AuditCenter = lazy(() => import('./pages/AuditCenter'));

// Suspense fallback dengan branding Kansha (lightweight, no extra deps)
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-loading">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-2 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm text-slate-500 italic" style={{ fontFamily: 'Georgia, serif' }}>Memuat halaman...</p>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Sync login/logout across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token removed in another tab → logout here too
          setUser(null);
        } else if (!user) {
          // Token added in another tab → check auth
          checkAuth();
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  // Auto-refresh data when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && user) {
        // Dispatch custom event so pages can refresh their data
        window.dispatchEvent(new Event('tab-focus'));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user]);

  const checkAuth = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login onLogin={setUser} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      <EntityProvider>
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
            {/* Kasir Lapangan bisa akses transaksi pembelian, transaksi lapangan, dan piutang */}
            {user.role === 'kasir_lapangan' ? (
              <>
                <Route index element={<Navigate to="/transactions" replace />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="field-transactions" element={<FieldTransactions />} />
                <Route path="receivables" element={<Receivables />} />
                <Route path="employee-portal" element={<EmployeePortal />} />
                <Route path="settings" element={<Settings />} />
              </>
            ) : (
              <>
                <Route index element={<Dashboard />} />
                <Route path="cashflow" element={<Cashflow />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="debts" element={<Debts />} />
                <Route path="receivables" element={<Receivables />} />
                {/* Investors - only for owner, admin tidak boleh akses */}
                {user.role === 'owner' ? (
                  <Route path="investors" element={<Investors />} />
                ) : (
                  <Route path="investors" element={<Navigate to="/" replace />} />
                )}
                <Route path="taxes" element={<Taxes />} />
                <Route path="hr" element={<HR />} />
                <Route path="operational" element={<Operational />} />
                <Route path="spsi" element={<SPSIPage />} />
                <Route path="other-income" element={<OtherIncome />} />
                <Route path="reports" element={<Reports />} />
                <Route path="profit-loss" element={<ProfitLoss />} />
                <Route path="stock-adjustment" element={<StockAdjustment />} />
                <Route path="bulk-import" element={<BulkImport />} />
                <Route path="intelligence-lab" element={<IntelligenceLab />} />
                <Route path="audit-center" element={<AuditCenter />} />
                <Route path="menu-settings" element={<MenuSettings />} />
                <Route path="field-transactions" element={<FieldTransactions />} />
                <Route path="employee-portal" element={<EmployeePortal />} />
                <Route path="settings" element={<Settings />} />
                {/* Owner and Admin can access owner-expenses */}
                {(user.role === 'owner' || user.role === 'admin') && (
                  <Route path="owner-expenses" element={<OwnerExpenses />} />
                )}
                {/* Owner only routes */}
                {user.role === 'owner' && (
                  <>
                    <Route path="users" element={<UserManagement />} />
                    <Route path="audit-log" element={<AuditLog />} />
                    <Route path="data-migration" element={<DataMigration />} />
                  </>
                )}
              </>
            )}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </EntityProvider>
    </div>
  );
}

export default App;
